import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { UnpackList, UnpackListItem } from './useUnpackList'

// Sibling to useUnpackList. useUnpackList finds "the active list for a
// household" (most recent uncompleted). useUnpackListById loads a specific
// list by id — used by spontaneous-trip mode where the page receives the
// list id via the route. Both hooks return identical shapes so the Unpack
// page can swap between them transparently.
export function useUnpackListById(listId: string | null): {
  list:    UnpackList
  items:   UnpackListItem[]
  loading: boolean
  error:   string | null
  refresh: () => void
} {
  const [list,    setList]    = useState<UnpackList>(null)
  const [items,   setItems]   = useState<UnpackListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState<string | null>(null)
  const [nonce,   setNonce]   = useState(0)

  useEffect(() => {
    if (!listId) { setLoading(false); return }

    let cancelled = false
    setLoading(true)
    setError(null)

    ;(async () => {
      const { data: listRow, error: listErr } = await supabase
        .from('shopping_lists')
        .select('id, household_id, generated_at')
        .eq('id', listId)
        .maybeSingle()

      if (cancelled) return

      if (listErr || !listRow) {
        setList(null)
        setItems([])
        setLoading(false)
        if (listErr) {
          console.error('[useUnpackListById] list query failed:', listErr)
          setError(listErr.message)
        }
        return
      }

      // Mirrors the is_impulse_buy fallback in useUnpackList — when the
      // unpack_spend_columns migration hasn't been pushed to the live DB,
      // the SELECT errors and we retry without the column.
      let rows: any[] | null = null
      let rowsErr: any = null
      let hasImpulseColumn = true

      {
        const r = await supabase
          .from('list_items')
          .select(`
            id,
            status,
            is_impulse_buy,
            pantry_items(id, name, display_category)
          `)
          .eq('list_id', listRow.id)
          .neq('status', 'removed')
        rows    = r.data
        rowsErr = r.error
      }

      if (rowsErr && /is_impulse_buy/i.test(rowsErr.message ?? '')) {
        console.warn(
          '[useUnpackListById] is_impulse_buy column is missing in your Supabase DB. ' +
          'Apply supabase/migrations/20260423180000_unpack_spend_columns.sql ' +
          '(via `supabase db push` or the Supabase SQL editor). Falling back without the column.'
        )
        hasImpulseColumn = false
        const r = await supabase
          .from('list_items')
          .select(`
            id,
            status,
            pantry_items(id, name, display_category)
          `)
          .eq('list_id', listRow.id)
          .neq('status', 'removed')
        rows    = r.data
        rowsErr = r.error
      }

      if (cancelled) return

      if (rowsErr) {
        console.error('[useUnpackListById] items query failed:', rowsErr)
        setList({
          id:          listRow.id,
          householdId: listRow.household_id,
          generatedAt: listRow.generated_at,
        })
        setItems([])
        setError(rowsErr.message)
        setLoading(false)
        return
      }

      const mapped: UnpackListItem[] = (rows ?? [])
        .filter((r: any) => r.pantry_items)
        .map((r: any) => {
          const pi = r.pantry_items as any
          const raw = pi.display_category as string | null | undefined
          return {
            id:              r.id,
            pantryItemId:    pi.id,
            name:            pi.name,
            category:        raw ?? null,
            categoryLabel:   raw
              ? raw.charAt(0).toUpperCase() + raw.slice(1).replace(/_/g, ' ')
              : null,
            confirmedBought: r.status === 'checked',
            isImpulse:       hasImpulseColumn ? !!r.is_impulse_buy : false,
          }
        })

      setList({
        id:          listRow.id,
        householdId: listRow.household_id,
        generatedAt: listRow.generated_at,
      })
      setItems(mapped)
      setLoading(false)
    })()

    return () => { cancelled = true }
  }, [listId, nonce])

  return { list, items, loading, error, refresh: () => setNonce(n => n + 1) }
}
